import React, { useContext, useEffect, useState } from "react";
import currency from "currency.js";
import { UseFormReturn, useFieldArray } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { FormInput, registerObject } from "go-form";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { ReactComponent as EditSVG } from "go-core/images/svg/actions/edit.svg";
import { ReactComponent as RemoveSVG } from "go-core/images/svg/actions/remove.svg";
import { ReactComponent as DropdownSVG } from "go-core/images/svg/dropdown.svg";
import FormMoneyInput from "go-form/components/FormMoneyInput";
import FormNumberInput from "go-form/components/FormNumberInput";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { apiOrganization } from "../../../../../../../../../../../../../services/Api/Organization/apiOrganization";
import {
	ProductApi,
	PurchaseCorrectionApi,
	PurchaseCorrectionItemApi,
	TaxApi,
} from "../../../../../../../../../../../../../services/Api/Organization/types";
import { isItemRowIndexShown, showMore } from "../../../../../../../utils/itemsTableUtils";
import { PurchaseContext } from "../../../../../pages/Preview";
import { getPreviousCorrection } from "../utils";
import PurchaseCorrectionFormItemsTableSummary from "./PurchaseCorrectionFormItemsTableSummary";

interface Props {
	form: UseFormReturn<PurchaseCorrectionApi>;
	taxes: TaxApi[];
	setShowChangeProductModal: (value: number | null) => void;
	handleChangeProduct: (product: ProductApi, index: number) => void;
	searchProducts: (
		search: string,
		params: Record<string, any>,
		options?: Record<string, any>
	) => Promise<ProductApi[]>;
}

const PurchaseCorrectionFormItemsTableBody = ({
	form,
	taxes,
	setShowChangeProductModal,
	handleChangeProduct,
	searchProducts,
}: Props) => {
	const {
		control,
		watch,
		setValue,
		register,
		formState: { errors },
	} = form;
	const { fields, remove, append } = useFieldArray({
		control,
		name: "items",
		keyName: "key",
	});
	const { t } = useTranslation();
	const watchedItems = watch("items");
	const { purchase, purchaseCorrections } = useContext(PurchaseContext);
	const organization = useSelector(selectOrganization);
	const [shownIndexes, setShownIndexes] = useState<number[]>([]);
	const watchedItemsAsString = JSON.stringify(watchedItems);

	useEffect(() => {
		if (checkIfAddNewItem()) {
			append({
				product: null,
				price_gross_money: null,
				tax: null,
				volume: null,
				tax_id: null,
				product_id: null,
				quantity: null,
				id: new Date().getTime(),
				total_price_gross_money: null,
				price_net_money: null,
				total_price_net_money: null,
			});
		}
	}, [watchedItemsAsString]);

	const checkIfAddNewItem = () => {
		const prevItem = watchedItems[watchedItems?.length - 1];

		if (prevItem.item_id) return true;

		const isTotalPriceNetMoney =
			prevItem.total_price_net_money?.amount !== null &&
			prevItem.total_price_net_money?.amount !== undefined &&
			!isNaN(prevItem?.total_price_net_money?.amount);
		const isTotalPriceGrossMoney =
			prevItem.total_price_gross_money?.amount !== null &&
			prevItem.total_price_gross_money?.amount !== undefined &&
			!isNaN(prevItem?.total_price_gross_money?.amount);
		const isPriceGrossMoney =
			prevItem.price_gross_money?.amount !== null &&
			prevItem.price_gross_money?.amount !== undefined &&
			!isNaN(prevItem?.price_gross_money?.amount);
		const isPriceNetMoney =
			prevItem.price_net_money?.amount !== null &&
			prevItem.price_net_money?.amount !== undefined &&
			!isNaN(prevItem?.price_net_money?.amount);

		return (
			prevItem?.product_id ||
			isPriceNetMoney ||
			isTotalPriceNetMoney ||
			isPriceGrossMoney ||
			isTotalPriceGrossMoney ||
			prevItem.quantity !== null ||
			prevItem.tax_id
		);
	};

	const renderVolumeInput = (item: PurchaseCorrectionItemApi, index: number) => {
		const volumes = item?.product?.volumes || [];

		if (volumes.length > 0) {
			const volumesOptions = volumes.map((volume) => ({
				id: volume,
				label: volume,
			}));
			return (
				<FormSelectGroup
					name={`items.${index}.volume`}
					errors={errors}
					openMenuOnFocus
					getOptionValue={(option) => option.id}
					getOptionLabel={(option) => option.label}
					defaultValue={{
						label: item?.product?.default_volume,
						id: item?.product?.default_volume,
					}}
					options={volumesOptions}
					control={control}
					data-testid={`items.${index}.volume`}
				/>
			);
		}

		return (
			<FormNumberInput
				decimalScale={5}
				errors={errors}
				name={`items.${index}.volume`}
				suffix={
					item?.product?.measure_type ? t(`enums.products.measure_types.${item?.product?.measure_type}`) : ""
				}
				control={control}
			/>
		);
	};

	const handleChangeTax = async (id: number, tax: TaxApi, index: number) => {
		registerObject(register, `items.${index}.tax`, ["amount", "name", "code", "id"]);
		setValue(`items.${index}.tax`, tax);
		setValue(`items.${index}.tax_id`, id, { shouldDirty: true });
	};

	const searchTaxes = async (search: string, params: Record<string, any>, options?: Record<string, any>) => {
		return apiOrganization.getTaxSearchSelect(search, params, {
			cancelToken: options?.token,
		});
	};

	const getItemPreviousPrice = (item: PurchaseCorrectionItemApi) => {
		if (item.item_id) {
			const purchaseItem = purchase.items.find(({ id }) => item.item_id === id);
			if (!purchaseItem) return "-";

			const money = purchase.tax_gross ? purchaseItem.price_gross_money : purchaseItem.price_net_money;
			return FormatMoney(money);
		}
		return "-";
	};

	const handleRemoveItem = (index: number) => {
		remove(index);
	};

	const handleChangeTotalPrice = (
		value: number | undefined,
		item: PurchaseCorrectionItemApi,
		index: number,
		isQuantity?: boolean
	) => {
		if (isQuantity) {
			const priceAmount = purchase.tax_gross ? item?.price_gross_money?.amount : item?.price_net_money?.amount;
			setValue(
				purchase.tax_gross ? `items.${index}.total_price_gross_money` : `items.${index}.total_price_net_money`,
				{
					amount: !priceAmount ? 0 : value ? currency(value).multiply(priceAmount).value : 0,
					currency: purchase.currency,
				}
			);
		} else
			setValue(
				purchase.tax_gross ? `items.${index}.total_price_gross_money` : `items.${index}.total_price_net_money`,
				{
					amount: !item?.quantity ? 0 : value ? currency(value).multiply(item?.quantity || 0).value : 0,
					currency: purchase.currency,
				}
			);
	};

	const handleChangeUnitPrice = async (value: number | undefined, item: PurchaseCorrectionItemApi, index: number) => {
		let newValue = 0;
		if (value && item.quantity) newValue = currency(value).divide(item.quantity).value;
		setValue(purchase.tax_gross ? `items.${index}.price_gross_money` : `items.${index}.price_net_money`, {
			amount: newValue,
			currency: purchase.currency,
		});
	};

	const renderProduct = (item: PurchaseCorrectionItemApi, index: number) => {
		if (item.item_id || item.product?.id) {
			return (
				<>
					<DropdownSVG
						className="icon dropdown-icon"
						id={`icon-${index}`}
						style={{
							transform: isItemRowIndexShown(shownIndexes, index) ? "rotate(180deg)" : "unset",
						}}
						onClick={() => showMore(shownIndexes, setShownIndexes, index)}
					/>
					<Link
						rel="noreferrer"
						target="_blank"
						to={`/${organization.id}/products/${item.product_id}/edit`}
						className={`${item.id ? "ms-2" : "mx-2"}`}
					>
						{item.product?.name}
					</Link>
					{!item.item_id && (
						<EditSVG className="icon edit-icon" onClick={() => setShowChangeProductModal(index)} />
					)}
				</>
			);
		}
		return (
			<>
				<FormSelectGroup
					name={`items.${index}.product.name`}
					errors={errors}
					onChange={(_, product) => handleChangeProduct(product, index)}
					getOptionValue={(option) => option.id}
					defaultValue={{
						label: item.product?.name,
						id: item.product?.id,
					}}
					loadOptions={searchProducts}
					control={control}
					data-testid={`items.${index}.product.name`}
				/>
				<FormInput
					type="hidden"
					errors={errors}
					register={register}
					name={`items.${index}.product_id`}
					defaultValue={item.product_id || undefined}
				/>
			</>
		);
	};

	const getItemQuantity = (item: PurchaseCorrectionItemApi) => {
		if (
			!purchaseCorrections ||
			purchaseCorrections?.length < 1 ||
			!getPreviousCorrection(watch("id"), purchaseCorrections)
		) {
			if (!item.item_id) return "-";

			const purchaseItem = purchase.items.find(({ id }) => item.item_id === id);
			return purchaseItem?.quantity ?? "-";
		} else if (!item.item_id) {
			const previousCorrectionItem = getPreviousCorrection(watch("id"), purchaseCorrections)?.items?.find(
				(correctionItem) => correctionItem.item_id === item.item_id && correctionItem.volume === item.volume
			);

			return previousCorrectionItem?.quantity ?? "-";
		}

		const previousCorrectionItem = getPreviousCorrection(watch("id"), purchaseCorrections)?.items?.find(
			({ item_id }) => item_id === item.item_id
		);

		return previousCorrectionItem?.quantity ?? "-";
	};

	return (
		<tbody>
			{watchedItems.map((item, index) => {
				const measureType = item.product ? t(`enums.products.measure_types.${item.product.measure_type}`) : "";
				const itemTax = taxes.find(({ id }) => id === item.tax_id);

				return (
					<React.Fragment key={fields[index].key}>
						<tr>
							<td>
								<FormInput
									type="hidden"
									errors={errors}
									register={register}
									name={`items.${index}.id`}
									defaultValue={item.id || undefined}
								/>
								{renderProduct(item, index)}
							</td>
							<td>{item.item_id ? `${item.volume} ${measureType}` : renderVolumeInput(item, index)}</td>
							<td>{getItemQuantity(item)}</td>
							<td>
								<FormNumberInput
									errors={errors}
									name={`items.${index}.quantity`}
									suffix=""
									control={control}
									onChange={(value) => handleChangeTotalPrice(value, item, index, true)}
								/>
							</td>
							<td>
								<FormSelectGroup
									name={`items.${index}.tax_id`}
									errors={errors}
									onChange={(id, tax) => handleChangeTax(id, tax, index)}
									isClearable={false}
									getOptionValue={(option) => option.id}
									defaultValue={{
										id: itemTax?.id,
										label: itemTax?.name,
									}}
									loadOptions={searchTaxes}
									control={control}
									data-testid={`items.${index}.tax_id`}
								/>
							</td>
							<td>{getItemPreviousPrice(item)}</td>
							<td>
								<FormMoneyInput
									errors={errors}
									name={
										purchase.tax_gross
											? `items.${index}.price_gross_money.amount`
											: `items.${index}.price_net_money.amount`
									}
									onChange={(value) => {
										handleChangeTotalPrice(value, item, index);
									}}
									control={control}
									defaultValue={
										purchase.tax_gross
											? item?.price_gross_money?.amount
											: item?.price_net_money?.amount
									}
									currency={purchase.currency}
								/>
							</td>
							<td>
								<FormMoneyInput
									errors={errors}
									name={
										purchase.tax_gross
											? `items.${index}.total_price_gross_money.amount`
											: `items.${index}.total_price_net_money.amount`
									}
									onChange={(value) => {
										handleChangeUnitPrice(value, item, index);
									}}
									defaultValue={
										purchase.tax_gross
											? item?.total_price_gross_money?.amount
											: item?.total_price_net_money?.amount
									}
									control={control}
									currency={purchase.currency}
								/>
							</td>
							<td className="action">
								{fields.length > 1 && !item.item_id && (
									<RemoveSVG className="icon" onClick={() => handleRemoveItem(index)} />
								)}
							</td>
						</tr>
						{isItemRowIndexShown(shownIndexes, index) && (
							<tr>
								<td>
									<FormInput
										errors={errors}
										register={register}
										className="comment-input"
										name={`items.${index}.comment`}
										defaultValue={item?.comment}
										placeholder={t("common.word.comment.placeholder")}
									/>
								</td>
								<td colSpan={7} />
								<td className="action" />
							</tr>
						)}
					</React.Fragment>
				);
			})}
			<PurchaseCorrectionFormItemsTableSummary watchedItems={watchedItems} form={form} />
		</tbody>
	);
};

export default PurchaseCorrectionFormItemsTableBody;
